import {
    Category,
    container,
    RdssCartService,
    Filter,
    ListingProduct,
    PaginationResponse,
    ProductSearchService,
    QuickFilter,
    QueryFilter,
    RequestQuery,
    SortingOption,
    NumberOrString,
    RdssSimpleCart,
} from '@rdss/client'
import { registerHistoryNavigationListener, unRegisterHistoryNavigationListener, updateUrl } from '@rdss/core'
import { Build, Listen, Component, h, Host, Prop, State, Event, EventEmitter, Element } from '@stencil/core'
import { store, trans } from '~/store'
import {
    trackAdobeEvent,
    trackingProducts,
    isSponsoredProduct,
    trackCitrusAd,
    hasTrackingDataChanged,
} from '~/utils/tracking'

@Component({
    tag: 'zr-listing-page',
    styleUrl: 'zr-listing-page.scss',
    scoped: true,
})

/**
 * The zooroyal listing page.
 */
export class ZrListingPage {
    /**
     * Skip sponsored products.
     */
    private skipSponsored: boolean | undefined

    /**
     * The product search service.
     */
    @Prop() readonly productSearchService = container.resolve(ProductSearchService)

    /**
     * The filter query parameters from smarty template.
     */
    @Prop() filter?: string | object

    /**
     * The filter from api Response.
     */
    @State() apiFilter?: Filter[]

    /**
     * The pagination query parameters.
     */
    @Prop() pager?: string

    /**
     * The sorting query parameter. If not specified by the user we let the
     * backend decide which sorting to use as the default.
     */
    @Prop({ mutable: true }) sort?: number

    /**
     * Text that will be displayed if there are no products to show.
     */
    @Prop() searchNoResultInfo? = trans(
        'frontend/listing/listing_actions.noSearchResults',
        'Oh Nein! <br/><br/> Wir haben leider nichts gefunden, das zu deiner Suche passt.'
    )

    /**
     * The search query.
     */
    @Prop() search?: string

    /**
     * Bool if Listing Page is manufacturer Listing.
     */
    @Prop() ismanufacturerlisting = false

    /**
     * The cart service.
     */
    @Prop() readonly cartService = container.resolve(RdssCartService)

    /**
     * The pagination object.
     */
    @State() pagination: Partial<PaginationResponse> = { page: 1, objectsPerPage: 24 }

    /**
     * The prepared and finalized product-data.
     */
    @State() products: ListingProduct[]

    /**
     * List of possible categories.
     */
    @State() categories: Category[]

    /**
     * The filter state.
     */
    @State() innerFilter?: QueryFilter = {}

    /**
     * The sorting options.
     */
    @State() sorting?: SortingOption[]

    /**
     * List of possible categories.
     */
    @State() quickFilters: QuickFilter[]

    /**
     * The last tracking object.
     */
    @State() lastTrackingObject: any

    /**
     * The quicklink parameter information.
     */
    @State() quicklink: string | null | undefined = undefined

    /**
     * The event emitter for a successful search to store the query.
     */
    @Event() userSearchSuccess: EventEmitter<{ value: string }>

    /**
     * The host element to reference component elements inside the component.
     */
    @Element() private host: HTMLElement

    /**
     * The aria status element.
     */
    private ariaStatusElement: HTMLElement

    /**
     * Handles change of sorting.
     */
    @Listen('sortingChanged')
    async handleSortingChanged({ detail }: CustomEvent<{ sort: number }>) {
        this.sort = detail.sort
        await this.performSearch()
        this.trackListingPageEvent()
    }

    /**
     * The event listener for pagination page select events.
     */
    @Listen('pageSelected')
    async handlePaginatorPageSelect(event: CustomEvent) {
        this.pagination.page = event.detail.page
        await this.performSearch()
        this.focusFirstProductTile()
        this.trackListingPageEvent()
    }

    /**
     * Focus the first product tile.
     */
    focusFirstProductTile() {
        const tile = this.host.querySelector('zr-product-tile')
        if (tile) {
            tile.scrollIntoView({ behavior: 'smooth' })
            tile.shadowRoot.querySelector('a').focus()
        }
    }

    /**
     * Get Sorting Label.
     */
    getSortingLabel() {
        return this.sorting.filter(item => item.selected).shift()?.label || 'default'
    }

    /**
     * Track Listing Page Event.
     */
    trackListingPageEvent(){
        const trackingEvent = 'customListingPageView'
        let trackingObject: any = {
            ...window.pageData,
            page: {
                ...window.pageData.page,
                page: this.pagination.page.toString(),
            },
            products: trackingProducts(this.products),
            sorting: this.getSortingLabel(),
            quicklink: this.quicklink ?? '',
            productSearch: { selectedFilter: [] },
        }
        if (this.search && !this.categories?.length) {
            trackingObject = {
                ...trackingObject,
                search: {
                    category: 'productsearch',
                    nrOfResults: this.pagination.objectCount.toString(),
                    sorting: this.getSortingLabel(),
                    term: this.search,
                    type: 'searchresultpage',
                },
            }
        }
        this.apiFilter?.filter(filter => filter.id.startsWith('b') || filter.id.startsWith('p'))
            .forEach((filter) => {
                if (filter.options) {
                    const optionValues = filter.options
                        .filter(option => option.selected)
                        .map(option => option.label)
                    if (optionValues.length) {
                        trackingObject.productSearch.selectedFilter.push({
                            type: filter.label,
                            values: optionValues,
                        })
                    }
                }
            })
        if (hasTrackingDataChanged(this.lastTrackingObject, trackingObject)) {
            this.lastTrackingObject = trackingObject
            trackAdobeEvent(trackingEvent, trackingObject)
        }
    }

    /**
     * The event listener for quickfilter selection.
     */
    @Listen('quickFilterClicked')
    async handleQuickFilterClicked({ detail }: CustomEvent) {
        this.innerFilter = { categories: [detail] }
        this.pagination.page = 1
        await this.performSearch()
    }

    /**
     * The event listener for filter selection.
     */
    @Listen('filterChanged')
    async handleFilterClicked({ detail }: CustomEvent<{
        filterId: string,
        filterValueId: NumberOrString,
        filterValue: string,
        filterRemoved: boolean
    }>) {
        const filterPrepare = this.innerFilter
        const filterValueIdAsString = detail.filterValueId.toString()

        if (!filterPrepare[detail.filterId]) {
            filterPrepare[detail.filterId] = []
        }

        const unifiedFilters = filterPrepare[detail.filterId].map(
            (value: NumberOrString) => value.toString()
        )

        if (unifiedFilters.includes(filterValueIdAsString)) {
            filterPrepare[detail.filterId] = filterPrepare[detail.filterId].filter(
                (value: NumberOrString) => {
                    return value.toString() !== filterValueIdAsString
                }
            )
        } else {
            filterPrepare[detail.filterId].push(detail.filterValueId)
        }

        if (filterPrepare[detail.filterId].length <= 0) {
            delete filterPrepare[detail.filterId]
        }

        this.innerFilter = filterPrepare
        this.pagination.page = 1

        if (!detail.filterId.startsWith('b') || detail.filterRemoved) {
            await this.performSearch()
        }
        if (detail.filterRemoved) {
            this.trackListingPageEvent()
        }
    }

    /**
     * Perform Search on filter close.
     */
    @Listen('filterClosed')
    async handleFilterClosed({ detail }: CustomEvent<{
        doSearch?: boolean
    }>) {
        if(detail?.doSearch) {
            await this.performSearch()
        }
        this.trackListingPageEvent()
    }

    /**
     * The event listener for clearing all filter.
     */
    @Listen('clearFilter')
    async handleClearFilter() {
        const filter = this.innerFilter
        for (const filterId in filter) {
            if (filterId.startsWith('p') || filterId.startsWith('b')) {
                delete filter[filterId]
            }
        }
        this.innerFilter = filter
        await this.performSearch()
        this.trackListingPageEvent()
    }

    /**
     * Checks if filter is active.
     */
    isFilterActive(): boolean {
        for (const filterId in this.innerFilter) {
            if (filterId.startsWith('p') || filterId.startsWith('b')) {
                return true
            }
        }
        return false
    }

    /**
     * Component will render lifecycle event.
     */
    componentWillRender() {
        if (this.search && !this.categories?.length && this.products?.length) {
            this.userSearchSuccess.emit({
                value: this.search,
            })
        }
    }

    /**
     * Component will load lifecycle event.
     */
    async componentWillLoad() {
        if (this.filter) this.innerFilter = typeof this.filter === 'string' ? JSON.parse(this.filter) : this.filter
        if (this.pager) this.pagination = JSON.parse(this.pager)

        registerHistoryNavigationListener()
        await this.performSearch()
        if (Build.isBrowser) {
            this.trackListingPageEvent()
        }
    }

    /**
     * Component disconnect.
     */
    disconnectedCallback() {
        unRegisterHistoryNavigationListener()
    }

    /**
     * Perform search with filter and pagination parameters.
     */
    private performSearch = async () => {
        this.host.ariaBusy = 'true'
        const query: RequestQuery = {
            search: {
                'term': this.search,
            },
            filter: this.innerFilter,
            sort: this.sort,
            translations: [
                'frontend/plugins/RdssUi',
                'frontend/detail/index',
                'frontend/listing/addtocart',
                'frontend/listing/listing_actions',
                'frontend/detail/product-variants',
                'frontend/listing/product-box',
                'frontend/listing/filter_supplier',
                'frontend/index/cart',
            ],
            pagination: this.pagination,
            embed: ['products', 'translations'],
        }

        if (Build.isBrowser && this.skipSponsored === undefined) {
            const url = new URL(window.location.href)
            this.skipSponsored = url.searchParams.get('skipsponsored') === 'true'
        }
        if (this.skipSponsored) {
            query.skipsponsored = true
        }

        if (this.innerFilter?.categories?.length > 0) {
            query.embed.push('categories')
        }

        return this.productSearchService.find(query)
            .then(async ({
                quickFilter,
                filter,
                sorting,
                _embedded,
                pagination,
                seoUrl,
            }) => {
                this.products = _embedded.products as ListingProduct[]
                this.categories = _embedded.categories as Category[]
                store.state.translations = { ...store.state.translations, ..._embedded.translations }
                // this.subcategories = _embedded.subcategories as Category[]
                this.apiFilter = filter as Filter[]
                this.sorting = sorting
                this.pagination = pagination
                this.quickFilters = Object.values(quickFilter) as QuickFilter[]

                if (this.quicklink === undefined) {
                    const currentUrl = new URL(location.href)
                    this.quicklink = currentUrl.searchParams.get('quicklink')
                }
                if (seoUrl) updateUrl(seoUrl as string)

                this.setFilterTranslation()

                if (Build.isBrowser) {
                    this.products.forEach((product) => {
                        if (isSponsoredProduct(product)) {
                            trackCitrusAd('impression', product)
                        }
                    })
                }
                store.state.cart = (await this.cartService.getCart() as unknown as RdssSimpleCart)
            })
            .catch((e) => {
                if (Build.isBrowser && this.ariaStatusElement) {
                    this.ariaStatusElement.textContent = trans('frontend/listing/listing_messages.errorLoadingProducts',
                        'Es gab ein Problem beim Laden der Produkte.')
                }
                console.error('Failed fetching productSearchService: ', e)
            })
            .finally(() => {
                this.host.ariaBusy = 'false'
            })
    }

    /**
     * Set Translation for Brand Filter.
     */
    private setFilterTranslation = async () => {
        this.apiFilter?.map((filter) => {
            if (filter.id?.startsWith('b')) {
                filter.label = trans(
                    'frontend/listing/filter_supplier.FilterSupplierHeadline',
                    'Marken'
                )
            }
        })
    }

    /**
     * Renders the component.
     */
    render() {
        const hasProducts = this.products?.length > 0
        return (
            <Host aria-busy="false">
                <main>
                    <div aria-live="polite" hidden role="status" ref={el => (this.ariaStatusElement = el)}></div>
                    <div class="zr-listing-page__header">
                        {/* listing-page headline */}
                        {this.categories?.length > 0 &&
                            <h1 class="zr-listing-page__category-headline">{this.categories[0].name}</h1>
                        }

                        {/* search-result headline */}
                        {(this.search && !this.categories?.length) &&
                            (hasProducts
                                ? <h1 class="zr-listing-page__search-result-headline">
                                    {trans(
                                        'frontend/listing/listing_actions.searchResultHeadlinePrefix',
                                        'Suchergebnisse für'
                                    )} <br/>
                                    <strong class="zr-listing-page__search-term">"{this.search}"</strong> <br/>
                                    {trans(
                                        'frontend/listing/listing_actions.productsFoundLabel',
                                        '%s Produkte gefunden',
                                        [this.pagination.objectCount.toString()]
                                    )} <br/>
                                </h1>
                                : <h1 class="zr-listing-page__search-result-headline--no-result"
                                    innerHTML={trans(
                                        'frontend/listing/listing_actions.noSearchResults',
                                        'Oh Nein! <br/><br/> Wir haben leider nichts gefunden, das zu deiner Suche passt.'
                                    )}/>
                            )
                        }
                    </div>

                    {/* show quick-filter only for listing, not for search-results */}
                    {(this.quickFilters?.length > 0 && !this.search) &&
                        <zr-quick-filter-container categories={this.quickFilters}/>
                    }

                    {hasProducts &&
                        [
                            <div class={'zr-listing-page__filter-sorting-container'}>
                                {(this.apiFilter && this.apiFilter.length > 0) &&
                                    [
                                        <div class='zr-listing-page__filter'>
                                            <vx-filter-container
                                                buttonHeadingLabel={
                                                    trans(
                                                        'frontend/listing/listing_actions.ListingFilterButton',
                                                        'Filtern'
                                                    )
                                                }
                                                closeButtonLabel={
                                                    trans(
                                                        'frontend/listing/listing_actions.ListingActionsCloseFilter',
                                                        'Filter schließen'
                                                    )
                                                }
                                                executeFilterLabel={
                                                    trans(
                                                        'frontend/listing/listing_actions.ListingFilterButton',
                                                        'Filter anwenden'
                                                    )
                                                }
                                                showArticleLabel={
                                                    trans(
                                                        'frontend/listing/listing_actions.ListingActionsCloseFilterShowProducts',
                                                        `${this.pagination.objectCount} Produkt(e) anzeigen`,
                                                        [this.pagination.objectCount.toString()]
                                                    )
                                                }
                                                deleteAllButtonLabel={
                                                    trans(
                                                        'frontend/listing/listing_actions.ListingFilterResetAll',
                                                        'Alles löschen'
                                                    )
                                                }
                                                hideCategoryButton={
                                                    trans(
                                                        'frontend/listing/listing_actions.ListingFilterMore',
                                                        'Weitere Filter'
                                                    )
                                                }
                                                searchFilterPlaceholder={
                                                    trans(
                                                        'frontend/listing/listing_actions.searchFilterPlaceholder',
                                                        'Suche...'
                                                    )
                                                }
                                                filter={this.apiFilter}
                                                isManufacturerListing={this.ismanufacturerlisting}
                                            />
                                        </div>,
                                        <hr/>,
                                        <div class='zr-listing-page__selected-filter'>
                                            {this.apiFilter &&
                                                <vx-selected-filter
                                                    filter={this.apiFilter}
                                                    isManufacturerListing={this.ismanufacturerlisting}
                                                    deleteAllButtonLabel={
                                                        trans(
                                                            'frontend/listing/listing_actions.ListingFilterResetAll',
                                                            'Alles löschen'
                                                        )
                                                    }
                                                />
                                            }
                                        </div>,
                                    ]
                                }
                                {this.sorting?.length > 1 &&
                                    <div class='zr-listing-page__sorting-container'>
                                        <vx-sorting
                                            sortingLabel={
                                                trans('frontend/listing/listing_actions.sortingLabel',
                                                    'Sortierung')
                                            }
                                            sortingOptions={this.sorting}
                                        />
                                    </div>}
                            </div>,
                            <zr-listing-container
                                products={this.products}
                                searchTerm={this.search}
                                category={this.categories?.length ? this.categories?.shift().id : null}
                                hideSponsored={
                                    this.pagination.page > 1 ||
                                    this.isFilterActive() ||
                                    location.pathname.startsWith('/zooroyal-marken')
                                }
                            ></zr-listing-container>,
                        ]
                    }

                    {this.pagination && hasProducts &&
                        <vx-paginator
                            previousButton={trans('frontend/listing/listing_actions.ListingLinkPrevious', 'Zurück')}
                            nextButton={trans('frontend/listing/listing_actions.ListingLinkNext', 'Weiter')}
                            pageName={trans('frontend/listing/listing_actions.ListingTextSite', 'Page')}
                            pagination={this.pagination}
                        />
                    }
                </main>
            </Host>
        )
    }
}
